// What a CRM connection has been doing (admin): the job queue (retry a failed job), sync runs (stop one), the
// sync log and the conflicts a MANUAL policy left for a person to decide. Logs hold no tokens and no customer data
// beyond record ids (the API redacts them before they are stored).
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, Square } from 'lucide-react';
import { DataTable, ErrorBox } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { toast } from '@/store/toast';
import { useCan } from '@/store/auth';
import { usePoll } from '@/hooks/live';
import { enumLabel, fmtTime } from '@/lib/utils';
import { t } from '@/i18n';
import { crmApi, crmErrorText, type Connection } from './types';

const JOB_STATUSES = ['PENDING', 'RUNNING', 'RETRY', 'SUCCEEDED', 'DEAD', 'CANCELLED'];
const TONE: Record<string, 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  SUCCEEDED: 'success', SUCCESS: 'success', RUNNING: 'info', PENDING: 'warning', RETRY: 'warning', DEAD: 'destructive', FAILED: 'destructive', ERROR: 'destructive',
  CANCELLED: 'secondary', SKIPPED: 'secondary',
};
const Tone = ({ v }: { v: string }) => <Badge variant={TONE[v] ?? 'outline'}>{enumLabel(v)}</Badge>;

export function ActivityTab({ c }: { c: Connection }) {
  return (
    <div className="grid gap-4">
      <Jobs c={c} />
      <SyncRuns c={c} />
      <Conflicts c={c} />
      <Logs c={c} />
    </div>
  );
}

function Jobs({ c }: { c: Connection }) {
  const [status, setStatus] = React.useState('');
  const poll = usePoll(10_000);
  const q = useQuery({ queryKey: ['crm-jobs', c.id, status], refetchInterval: poll,
                       queryFn: () => crmApi<{ jobs: any[]; counts: { status: string; n: number }[] }>(`/connections/${c.id}/jobs`, { query: { status: status || undefined, limit: 100 } }) });
  const canSync = useCan('crm.sync');
  async function retry(id: number) {
    try { await crmApi(`/jobs/${id}/retry`, { method: 'POST', body: {} }); toast.ok(t('Queued again')); q.refetch(); } catch (e) { toast.error(crmErrorText(e)); }
  }
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <CardTitle>{t('Jobs')}</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {(q.data?.counts ?? []).map((x) => <span key={x.status} className="text-xs"><Tone v={x.status} /> {x.n}</span>)}
          <Select className="h-8 w-40" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('Job status')}>
            <option value="">{t('All')}</option>{JOB_STATUSES.map((s) => <option key={s} value={s}>{enumLabel(s)}</option>)}
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <ErrorBox error={q.error} />
        <DataTable rowKey={(r) => r.id} rows={q.data?.jobs} empty={t('No jobs.')} columns={[
          { key: 'id', label: '#' }, { key: 'job_type', label: t('Type'), render: (r) => enumLabel(r.job_type) },
          { key: 'status', label: t('Status'), render: (r) => <Tone v={r.status} /> },
          { key: 'attempts', label: t('Attempts'), render: (r) => `${r.attempts}/${r.max_attempts}` },
          { key: 'record', label: t('Record'), render: (r) => r.remote_record_id ?? r.correlation_id ?? '-' },
          { key: 'last_error', label: t('Error'), render: (r) => (r.last_error_code ? <span className="text-xs text-destructive" title={r.last_error ?? ''}>{r.last_error_code}</span> : '') },
          { key: 'next_retry_at', label: t('Next try'), render: (r) => (r.status === 'RETRY' || r.status === 'PENDING' ? fmtTime(r.next_retry_at) : '') },
          { key: 'updated_at', label: t('Updated'), render: (r) => fmtTime(r.updated_at) }]}
          actions={(r) => canSync && ['DEAD', 'CANCELLED', 'RETRY'].includes(r.status)
            ? <Button size="icon" variant="ghost" aria-label={t('Retry job {{id}}', { id: r.id })} onClick={() => retry(r.id)}><RotateCcw /></Button> : null} />
      </CardContent>
    </Card>
  );
}

function SyncRuns({ c }: { c: Connection }) {
  const poll = usePoll(15_000);
  const q = useQuery({ queryKey: ['crm-runs', c.id], refetchInterval: poll, queryFn: () => crmApi<{ runs: any[] }>(`/connections/${c.id}/sync-runs`, { query: { limit: 20 } }) });
  const canSync = useCan('crm.sync');
  async function stop(id: number) {
    try { await crmApi(`/sync-runs/${id}/stop`, { method: 'POST', body: {} }); toast.ok(t('Stopping')); q.refetch(); } catch (e) { toast.error(crmErrorText(e)); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>{t('Sync runs')}</CardTitle></CardHeader>
      <CardContent>
        <DataTable rowKey={(r) => r.id} rows={q.data?.runs} empty={t('No syncs yet.')} columns={[
          { key: 'created_at', label: t('Started'), render: (r) => fmtTime(r.started_at ?? r.created_at) },
          { key: 'remote_object', label: t('Object') }, { key: 'run_type', label: t('Type'), render: (r) => enumLabel(r.run_type) },
          { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> },
          { key: 'counts', label: t('Seen / new / updated / skipped'), render: (r) => `${r.records_seen} / ${r.records_created} / ${r.records_updated} / ${r.records_skipped}` },
          { key: 'problems', label: t('Conflicts / failed'), render: (r) => `${r.records_conflicted} / ${r.records_failed}` },
          { key: 'last_error', label: t('Error'), render: (r) => <span className="text-xs text-destructive">{r.last_error ?? ''}</span> }]}
          actions={(r) => canSync && (r.status === 'RUNNING' || r.status === 'QUEUED' || r.status === 'PAUSED')
            ? <Button size="icon" variant="ghost" aria-label={t('Stop sync {{id}}', { id: r.id })} onClick={() => stop(r.id)}><Square /></Button> : null} />
      </CardContent>
    </Card>
  );
}

function Conflicts({ c }: { c: Connection }) {
  const q = useQuery({ queryKey: ['crm-conflicts', c.id], queryFn: () => crmApi<{ conflicts: any[] }>(`/connections/${c.id}/conflicts`) });
  const canSync = useCan('crm.sync');
  async function resolve(id: number, resolution: 'RESOLVED_CRM' | 'RESOLVED_CC' | 'DISMISSED') {
    try { await crmApi(`/conflicts/${id}/resolve`, { body: { resolution } }); toast.ok(t('Resolved')); q.refetch(); } catch (e) { toast.error(crmErrorText(e)); }
  }
  const fieldsOf = (f: unknown): string => {
    const v = typeof f === 'string' ? (() => { try { return JSON.parse(f); } catch { return null; } })() : f;
    return v && typeof v === 'object' ? Object.keys(v as object).join(', ') : '';
  };
  if (!q.data?.conflicts.length) return null;
  return (
    <Card className="border-warning">
      <CardHeader><CardTitle>{t('Conflicts to decide')}</CardTitle></CardHeader>
      <CardContent>
        <DataTable rowKey={(r) => r.id} rows={q.data.conflicts} columns={[
          { key: 'detected_at', label: t('Found'), render: (r) => fmtTime(r.detected_at) },
          { key: 'remote', label: t('CRM record'), render: (r) => `${r.remote_object} ${r.remote_record_id}` },
          { key: 'local', label: t('Contact'), render: (r) => (r.local_record_id ? `#${r.local_record_id}` : '-') },
          { key: 'fields_json', label: t('Fields'), render: (r) => fieldsOf(r.fields_json) }]}
          actions={(r) => canSync ? <>
            <Button size="sm" variant="outline" onClick={() => resolve(r.id, 'RESOLVED_CRM')}>{t('Keep CRM')}</Button>
            <Button size="sm" variant="outline" onClick={() => resolve(r.id, 'RESOLVED_CC')}>{t('Keep call center')}</Button>
            <Button size="sm" variant="ghost" onClick={() => resolve(r.id, 'DISMISSED')}>{t('Dismiss')}</Button>
          </> : null} />
      </CardContent>
    </Card>
  );
}

function Logs({ c }: { c: Connection }) {
  const [outcome, setOutcome] = React.useState('');
  const q = useQuery({ queryKey: ['crm-logs', c.id, outcome], queryFn: () => crmApi<{ logs: any[] }>(`/connections/${c.id}/logs`, { query: { outcome: outcome || undefined, limit: 100 } }) });
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <CardTitle>{t('Sync log')}</CardTitle>
        <Select className="h-8 w-40" value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label={t('Result')}>
          <option value="">{t('All')}</option>{['SUCCESS', 'SKIPPED', 'CONFLICT', 'RETRY', 'FAILED', 'DEAD'].map((s) => <option key={s} value={s}>{enumLabel(s)}</option>)}
        </Select>
      </CardHeader>
      <CardContent>
        <DataTable rowKey={(r) => r.id} rows={q.data?.logs} empty={t('Nothing logged yet.')} columns={[
          { key: 'created_at', label: t('Time'), render: (r) => fmtTime(r.created_at) }, { key: 'operation', label: t('Operation') },
          { key: 'outcome', label: t('Result'), render: (r) => <Tone v={r.outcome} /> },
          { key: 'record', label: t('Record'), render: (r) => (r.remote_record_id ? `${r.remote_object ?? ''} ${r.remote_record_id}` : '-') },
          { key: 'error_code', label: t('Error'), render: (r) => (r.error_code ? <span className="text-xs text-destructive" title={r.message ?? ''}>{r.error_code}{r.http_status ? ` (${r.http_status})` : ''}</span> : '') },
          { key: 'duration_ms', label: t('ms') }]} />
      </CardContent>
    </Card>
  );
}
